import {Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef} from '@angular/core';
import {NgxPopupComponent} from '@components/shared/ngx-popups/ngx-popups/components/popup.component';
import {AlertService} from '@services/alert.service';
import { FileItem, FileUploader } from '@components/shared/file-upload';
import {SpreadingTemplateService} from "@services/spreading-template.service";
import {ConfirmationPopupComponent} from "@components/shared/popups/confirmation/confirmation-popup.component";
import {NgxPopupService} from "@components/shared/ngx-popups/ngx-popups/services/ngx-popup.service";

@Component({
    selector: 'custom-autonorm-mappings-upload',
    templateUrl: './custom-autonorm-mappings-upload.component.html',
    styleUrls: ['./custom-autonorm-mappings-upload.component.scss'],
    standalone: false
})
export class CustomAutonormMappingsUploadComponent implements OnInit {
  @Input() popup: NgxPopupComponent; // MANDATORY
  @Input() existingCustomAutonormMappingsS3Key: string = '';
  @Input() spreadingTemplateUuid: string = ''
  @Input() spreadingTemplateId: number
  @ViewChild('uploadInput', { static: false }) uploadInputHtml: ElementRef;

  @Output() onImport = new EventEmitter<any>();

  fileQueuedForUpload: boolean = false
  invalidFileType: boolean = false
  uploader: FileUploader;
  existingFileName: string = ''
  fileItemForUpload: FileItem = null
  newlySelectedFileName: string = ''


  constructor(
    private alertService: AlertService,
    private spreadingTemplateService: SpreadingTemplateService,
    private popupService: NgxPopupService) {
    // Initialize uploader in constructor to ensure it's available for ng2FileSelect directive
    this.uploader = new FileUploader({
      autoUpload: false,
      method: 'PUT',
      queueLimit: 1,
      disableMultipart: true,
      isHTML5: true,
      removeAfterUpload: false,
    });
  }

  ngOnInit() {
    this.existingFileName = this.parseFileNameFromS3Key(this.existingCustomAutonormMappingsS3Key)
    
    // Set up uploader event handlers
    this.uploader.onAfterAddingFile = (fileItem: FileItem) => {
      fileItem.withCredentials = false;
      console.log(fileItem)
      this.newlySelectedFileName = fileItem.file.name
      this.fileItemForUpload = fileItem
      // Acceptable MIME types for CSV and Excel
      const validMimeTypes = [
        'text/csv',
        'application/csv',
        'text/comma-separated-values',
        'application/vnd.ms-excel', // .xls or sometimes CSV
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel.sheet.macroenabled.12', // .xlsm
        'application/vnd.ms-excel.sheet.binary.macroenabled.12', // .xlsb
      ];
      // Acceptable extensions for CSV and Excel
      const validExtensions = ['.csv', '.xls', '.xlsx', '.xlsm', '.xlsb'];
      const fileType = fileItem.file.type.toLowerCase();
      const fileName = fileItem.file.name.toLowerCase();
      const hasValidMime = validMimeTypes.includes(fileType);
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      // For CSV and Excel files, MIME types can vary, so we accept if either MIME or extension is valid
      this.invalidFileType = !(hasValidMime || hasValidExtension);
      this.fileQueuedForUpload = !this.invalidFileType;
      if (this.invalidFileType) {
        this.uploader.clearQueue();
        this.newlySelectedFileName = '';
      }
    }
  }

  getFileChosenText(): string {
    // If a new file has been selected, show that
    if (this.newlySelectedFileName) {
      return this.newlySelectedFileName;
    }
    // If there's an existing file in the database, show that
    if (this.existingFileName) {
      return this.existingFileName;
    }
    // Otherwise, show the default message
    return 'No file chosen';
  }

  openFilePicker() {
    // When user cancels and chooses same file, the popup is not created, so fix by setting value to null
    // https://html.spec.whatwg.org/multipage/input.html#dom-input-value-filename
    this.uploadInputHtml.nativeElement.value = null;
    this.uploadInputHtml.nativeElement.click();
  }

  uploadDocument() {
    console.log(`Uploading file: ${this.fileItemForUpload.file.name}`)
    console.log(`File type: ${this.fileItemForUpload.file.type}`)
    console.log(`File size: ${this.fileItemForUpload.file.size} bytes`)
    console.log(`Template UUID: ${this.spreadingTemplateUuid}`)

    // If file type is empty, default to application/vnd.ms-excel for .xls files
    let fileType = this.fileItemForUpload.file.type || '';
    if (!fileType && this.fileItemForUpload.file.name.toLowerCase().endsWith('.xls')) {
      fileType = 'application/vnd.ms-excel';
      console.log('Empty MIME type detected for .xls file, defaulting to application/vnd.ms-excel')
    }
    
    this.spreadingTemplateService.generatePreSignedUrlForCustomAutonormMappingsUpload(
      this.fileItemForUpload.file.name, fileType, this.spreadingTemplateUuid).subscribe((data) => {
        this.uploader.setOptions({ url: data.s3UploadUrl });

        this.uploader.onErrorItem = (item: FileItem, response: string, status: number) => {
          this.uploader.clearQueue();
          this.alertService.error("An error occurred while uploading custom autonorm mappings")
        }

        this.uploader.onCompleteItem = (item: any, response: any, status: any, headers: any) => {
          this.uploader.clearQueue()
          if (this.existingFileName) {
            this.spreadingTemplateService.deleteCustomAutonormMappings(this.spreadingTemplateId).subscribe(result => {
              this.alertService.success(`Successfully removed ${this.existingFileName} as custom autonorm mappings`)
              this.performTemplateUpdateForCustomAutonormMappingsInfo(data.s3Key)
            }, error => {
              this.alertService.error(error.message)
            })
          } else {
            this.performTemplateUpdateForCustomAutonormMappingsInfo(data.s3Key)
          }
        }
        this.uploader.uploadAll()
    })
  }

  performTemplateUpdateForCustomAutonormMappingsInfo(s3Key) {
    this.spreadingTemplateService.updateTemplate(this.spreadingTemplateId, {'customAutonormMappingsKey': s3Key}).subscribe((response) => {
      //got to throw up an upload success toast and update the file name in the modal
      this.existingCustomAutonormMappingsS3Key = response[0].customAutonormMappingsKey
      this.existingFileName = this.parseFileNameFromS3Key(this.existingCustomAutonormMappingsS3Key)
      
      // Process the CSV to create structured JSON mappings
      this.spreadingTemplateService.processCustomAutonormMappings(this.spreadingTemplateId, s3Key).subscribe((processResponse) => {
        let successMessage = `${this.existingFileName} successfully uploaded and processed.`
        
        // Show warnings if any duplicates were found
        if (processResponse.warnings && processResponse.warnings.length > 0) {
          successMessage += ` Note: ${processResponse.warnings.join(' ')}`
        }
        
        this.alertService.success(successMessage)
        this.fileQueuedForUpload = false
        this.newlySelectedFileName = ''
      }, error => {
        this.alertService.error(`File uploaded but processing failed: ${error.message}`)
        this.fileQueuedForUpload = false
        this.newlySelectedFileName = ''
      })
    })
  }

  deleteExistingReferenceSheetUpload() {
    this.popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'REMOVE CUSTOM AUTONORM MAPPINGS',
        text: `Are you sure you want to remove ${this.existingFileName} from the template?`,
        confirmButtonText: 'Yes'
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.spreadingTemplateService.deleteCustomAutonormMappings(this.spreadingTemplateId).subscribe(result => {
              this.alertService.success(`Successfully removed ${this.existingFileName} as custom autonorm mappings`)
              this.existingFileName = ''
              this.existingCustomAutonormMappingsS3Key = ''
              this.newlySelectedFileName = ''
            }, error => {
              this.alertService.error(error.message)
            })
          }
        }
      },
    });
  }

  downloadReferenceSheet() {
    this.spreadingTemplateService.retrievePreSignedUrlForCustomAutonormMappingsDownload(this.existingFileName, this.existingCustomAutonormMappingsS3Key).subscribe(data => {
      console.log(data.referenceSheetUrl)
      const url = data.referenceSheetUrl;
      const link = document.createElement('a');
      link.href = url;
      link.download = this.existingFileName;
      link.target = '_blank';
      link.dispatchEvent(new MouseEvent('click'));
    },
    error => {
      this.alertService.error(error.message);
    })
  }

  close() {
    return this.popup.close(true, {'key':this.existingCustomAutonormMappingsS3Key})
  }

  parseFileNameFromS3Key(key) {
    if (!key || key === '') {
      return '';
    }
    return key.split('/').pop()
  }
}

